import { useCallback, useEffect, useRef, useState } from "react";
import { getCurrentDateString } from "../utils/PrescriptionHelpers";
import { addDays, addYears } from "../utils/dateUtils";
import { buildTransferRxPayload } from "../utils/PayloadBuilders";
import { TRANSFER_RX_DEFAULTS } from "../config/PrescriptionDefaults";
import {
  IPatientDetails,
  IPrescriberDetails,
  IMedicationDetails,
} from "../types/PrescriptionIngestionTypes";

const WRITTEN_DAYS_BEFORE_LAST_FILL = 10;

export interface ITransferDetailDefaults {
  quantityRemaining: string;
  lastFillDate: string;
  writtenDate: string;
  expirationDate: string;
}

/**
 * Transfer details that pass CATS auto-typing: remaining quantity equals
 * quantity x (refills remaining + 1), the last fill is one days-supply ago,
 * and the Rx expires one year after it was written.
 */
export const transferDetailDefaults = (
  quantity: string,
  daysSupply: string,
  refillsRemaining: string,
  today: string
): ITransferDetailDefaults => {
  const lastFillDate = addDays(today, -(Number(daysSupply) || 0));
  const writtenDate = addDays(lastFillDate, -WRITTEN_DAYS_BEFORE_LAST_FILL);
  return {
    quantityRemaining: remainingQuantity(quantity, refillsRemaining),
    lastFillDate,
    writtenDate,
    expirationDate: addYears(writtenDate, 1),
  };
};

export const remainingQuantity = (quantity: string, refillsRemaining: string): string =>
  String((Number(quantity) || 0) * ((Number(refillsRemaining) || 0) + 1));

/**
 * Custom hook for managing TransferRx form state and logic
 *
 * Encapsulates all the state and logic related to TransferRx prescriptions including:
 * - Source pharmacy details
 * - Transfer details (refills, quantities, dates)
 * - Payload building
 */
export const useTransferRxForm = () => {
  // Source Pharmacy State
  const [sourcePharmacyNCPDP, setSourcePharmacyNCPDP] = useState<string>(
    TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.NCPDP_ID
  );
  const [sourcePharmacyNPI, setSourcePharmacyNPI] = useState<string>(
    TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.NPI
  );
  const [sourcePharmacyBusinessName, setSourcePharmacyBusinessName] =
    useState<string>(TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.BUSINESS_NAME);
  const [sourcePharmacyAddressLine1, setSourcePharmacyAddressLine1] =
    useState<string>(TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.ADDRESS_LINE1);
  const [sourcePharmacyCity, setSourcePharmacyCity] = useState<string>(
    TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.CITY
  );
  const [sourcePharmacyState, setSourcePharmacyState] = useState<string>(
    TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.STATE
  );
  const [sourcePharmacyZipCode, setSourcePharmacyZipCode] = useState<string>(
    TRANSFER_RX_DEFAULTS.SOURCE_PHARMACY.ZIP_CODE
  );

  // Transfer Details State
  const [refillsRemaining, setRefillsRemaining] = useState("1");
  const [quantityRemaining, setQuantityRemaining] = useState("90");
  const [quantityTransferred, setQuantityTransferred] = useState("90");
  const [expirationDate, setExpirationDate] = useState(() => addYears(getCurrentDateString(), 1));
  const [writtenDate, setWrittenDate] = useState(getCurrentDateString());
  const [lastFillDate, setLastFillDate] = useState(getCurrentDateString());

  const updateWrittenDate = useCallback((date: string) => {
    setWrittenDate(date);
    if (date) {
      setExpirationDate(addYears(date, 1));
    }
  }, []);

  /** Prefills transfer details CATS can auto-type for the given medication; returns the written date. */
  const applyMedicationDefaults = useCallback(
    (quantity: string, daysSupply: string, refills: string): string => {
      const defaults = transferDetailDefaults(quantity, daysSupply, refills, getCurrentDateString());
      setQuantityRemaining(defaults.quantityRemaining);
      setQuantityTransferred(defaults.quantityRemaining);
      setLastFillDate(defaults.lastFillDate);
      setWrittenDate(defaults.writtenDate);
      setExpirationDate(defaults.expirationDate);
      return defaults.writtenDate;
    },
    []
  );

  const applyRemainingQuantity = useCallback((quantity: string, refills: string) => {
    const remaining = remainingQuantity(quantity, refills);
    setQuantityRemaining(remaining);
    setQuantityTransferred(remaining);
  }, []);

  /**
   * Build TransferRx payload from current form state
   */
  const buildTransferRxData = (
    patientDetails: IPatientDetails,
    prescriberDetails: IPrescriberDetails,
    medicationDetails: IMedicationDetails,
    placeOfService: string
  ) => {
    return buildTransferRxPayload({
      patientDetails,
      prescriberDetails,
      medicationDetails,
      placeOfService,
      sourcePharmacyNCPDP,
      sourcePharmacyNPI,
      sourcePharmacyBusinessName,
      sourcePharmacyAddressLine1,
      sourcePharmacyCity,
      sourcePharmacyState,
      sourcePharmacyZipCode,
      lastFillDate,
      refillsRemaining,
      quantityRemaining: {
        value: quantityRemaining,
        quantityUnitOfMeasure: { code: "C120263" },
        codeListQualifier: "87",
      },
      quantityTransferred: {
        value: quantityTransferred,
        quantityUnitOfMeasure: { code: "C120263" },
        codeListQualifier: "87",
      },
      writtenDate,
      expirationDate,
    });
  };

  return {
    // Source Pharmacy
    sourcePharmacyNCPDP,
    setSourcePharmacyNCPDP,
    sourcePharmacyNPI,
    setSourcePharmacyNPI,
    sourcePharmacyBusinessName,
    setSourcePharmacyBusinessName,
    sourcePharmacyAddressLine1,
    setSourcePharmacyAddressLine1,
    sourcePharmacyCity,
    setSourcePharmacyCity,
    sourcePharmacyState,
    setSourcePharmacyState,
    sourcePharmacyZipCode,
    setSourcePharmacyZipCode,

    // Transfer Details
    refillsRemaining,
    setRefillsRemaining,
    quantityRemaining,
    setQuantityRemaining,
    quantityTransferred,
    setQuantityTransferred,
    expirationDate,
    setExpirationDate,
    writtenDate,
    setWrittenDate: updateWrittenDate,
    lastFillDate,
    setLastFillDate,

    // Methods
    applyMedicationDefaults,
    applyRemainingQuantity,
    buildTransferRxData,
  };
};

interface IMedicationDefaultsSource {
  ndc: string;
  quantity: { value: string };
  daysSupply: string;
  writtenDate: string;
}

/**
 * Keeps the transfer details CATS-auto-typeable as the medication changes: picking a drug or
 * changing its quantity or days supply re-derives every default, changing refills only re-derives
 * the quantities, and the medication's written date mirrors the transfer's.
 */
export const useTransferRxDefaults = (
  transferForm: ReturnType<typeof useTransferRxForm>,
  medication: IMedicationDefaultsSource,
  onMedicationWrittenDateChange: (writtenDate: string) => void
) => {
  const { applyMedicationDefaults, applyRemainingQuantity, refillsRemaining, setWrittenDate } = transferForm;
  const refillsRef = useRef(refillsRemaining);
  refillsRef.current = refillsRemaining;

  useEffect(() => {
    if (medication.writtenDate) {
      setWrittenDate(medication.writtenDate);
    }
  }, [medication.writtenDate, setWrittenDate]);

  useEffect(() => {
    const writtenDate = applyMedicationDefaults(medication.quantity.value, medication.daysSupply, refillsRef.current);
    onMedicationWrittenDateChange(writtenDate);
  }, [
    medication.ndc,
    medication.quantity.value,
    medication.daysSupply,
    applyMedicationDefaults,
    onMedicationWrittenDateChange,
  ]);

  useEffect(() => {
    applyRemainingQuantity(medication.quantity.value, refillsRemaining);
  }, [medication.quantity.value, refillsRemaining, applyRemainingQuantity]);
};
